/* eslint-disable react/no-unescaped-entities */
import React, { FC, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import clsx from 'clsx';
import * as Yup from 'yup';

import AuthModel, { ForgotPasswordFormData } from '../../../models/auth';

import { AUTH_ERRORS } from '../../../Constants/ErrorMessages';
import AuthThankYou from '../AuthThankYou';
import Button from '../../Button';
import SectionWrapper from '../../SectionWrapper';
import { TMessagesData } from '../../../models/Message/api';
import TextField from '../../FormFields/TextField';
import Typo from '../../Typo';
import { getFieldError } from '../../FormFields/Utils';
import styles from './forgotPasswordForm.module.sass';
import useForgotPasswordActions from './useForgotPasswordActions';
import { useIsMobileViewport } from '../../../hooks/useIsMobileViewport';

const ValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email(AUTH_ERRORS.EMAIL_VALIDATION_NOT_MATCH)
    .required(AUTH_ERRORS.EMAIL_REQUIRED)
    .test('emailNotExist', AUTH_ERRORS.EMAIL_NOT_EXIST, async value => {
      if (!value) return true;
      const isExists = await AuthModel.isEmailExists(value);
      if (isExists === undefined) return true;
      return isExists;
    }),
});

interface IForgotPasswordProps {
  messages: TMessagesData;
}

const ForgotPasswordForm: FC<IForgotPasswordProps> = ({ messages }) => {
  const { isMobile } = useIsMobileViewport();
  const { handleResetPassword, state } = useForgotPasswordActions();

  const thankYouMessage = messages['Forgot Password - Check Email'];
  const form = useRef<FormikProps<Partial<ForgotPasswordFormData>>>(null);

  return (
    <Formik<Partial<ForgotPasswordFormData>>
      onSubmit={(val, helpers) => {
        if (!val.email) return;
        handleResetPassword.run({ values: val, formikHelpers: helpers });
      }}
      initialValues={{}}
      validationSchema={ValidationSchema}
      innerRef={form}
      enableReinitialize
    >
      {(formikProps: FormikProps<Partial<ForgotPasswordFormData>>) => {
        const error = getFieldError('email', formikProps);
        return (
          <form onSubmit={formikProps.handleSubmit} id="forgot_password_form">
            <SectionWrapper
              hasBodyMargin={!isMobile}
              classes={styles.rootContainer}
              childClasses={clsx(styles.rootChildContainer, {
                [styles.thanksRootChildContainer]: state === 'thank-you',
              })}
            >
              <Typo h1 classes={styles.heading}>
                Reset your password
              </Typo>
              <div
                className={clsx(styles.subheading, {
                  [styles.thanksSubHead]: state === 'thank-you',
                })}
              >
                <Typo medium>
                  Need a new password? Happens. Luckily you're just a quick click from getting back
                  on track.
                </Typo>
              </div>
              {state === 'forgot-password-form' && (
                <>
                  <Typo h2 classes={styles.fieldsTitle}>
                    Enter Your KnoPro Account Email Address
                  </Typo>
                  <div className={styles.fieldsContainer}>
                    <TextField
                      valueKey="email"
                      otherFieldProps={{
                        placeholder: 'Email Address',
                        onChange: formikProps.handleChange,
                        onBlur: formikProps.handleBlur,
                      }}
                      placeHolderColorBlack
                      error={error}
                      type="email"
                    />
                  </div>
                  <Button
                    type="submit"
                    theme="Filled - Lime Green Gradient"
                    classes={`${styles.resetBtn} ${
                      handleResetPassword.status === 'PROCESSING' && 'is-loading'
                    }`}
                  >
                    Reset My Password
                  </Button>
                  <div className={styles.linksContainer}>
                    <Typo normal>
                      <Button
                        isBtn={false}
                        underline
                        linkToUrl="contact-us"
                        classes="has-text-weight-bold"
                      >
                        Contact Support
                      </Button>
                    </Typo>
                  </div>
                </>
              )}
              {state === 'thank-you' && <AuthThankYou {...thankYouMessage} />}
            </SectionWrapper>
          </form>
        );
      }}
    </Formik>
  );
};

export default ForgotPasswordForm;
